import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { styleGuidesApi } from '../api/styleGuides';
import { useToast } from '../contexts/ToastContext';
import {
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  FaceSmileIcon,
} from '@heroicons/react/24/outline';

const STYLE_GUIDE_TABS = [
  { id: 'voice', label: 'Voice & Tone', icon: ChatBubbleBottomCenterTextIcon },
  { id: 'emojis', label: 'Emojis', icon: FaceSmileIcon },
  { id: 'phrases:use', label: 'Phrases to Use', icon: SparklesIcon },
  { id: 'phrases:avoid', label: 'Phrases to Avoid', icon: XMarkIcon },
  { id: 'formatting', label: 'Formatting', icon: DocumentTextIcon },
];

function StyleGuidePage() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState('voice');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');

  const { data: coreGuides, isLoading } = useQuery({
    queryKey: ['styleGuides', 'core'],
    queryFn: styleGuidesApi.getCore,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, content }) => styleGuidesApi.update(id, { content }),
    onSuccess: () => {
      queryClient.invalidateQueries(['styleGuides', 'core']);
      setIsEditing(false);
      showSuccess('Style guide updated successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to update style guide');
    },
  });

  const createMutation = useMutation({
    mutationFn: (guideData) => styleGuidesApi.create(guideData),
    onSuccess: () => {
      queryClient.invalidateQueries(['styleGuides', 'core']);
      setIsEditing(false);
      showSuccess('Style guide created successfully');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Failed to create style guide');
    },
  });

  const currentGuide = coreGuides?.[activeTab];

  const handleEdit = () => {
    setEditContent(JSON.stringify(currentGuide?.content || {}, null, 2));
    setIsEditing(true);
  };

  const handleSave = () => {
    try {
      const parsedContent = JSON.parse(editContent);
      if (currentGuide) {
        updateMutation.mutate({ id: activeTab, content: parsedContent });
      } else {
        createMutation.mutate({
          id: activeTab,
          type: activeTab,
          content: parsedContent,
        });
      }
    } catch {
      showError('Invalid JSON content');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
  };

  const renderVoiceContent = (content) => {
    if (!content) return <p className="text-gray-500 dark:text-gray-400 italic">No voice configuration set</p>;
    return (
      <div className="space-y-4">
        {content.tone && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Overall Tone</h4>
            <p className="mt-1 text-gray-900 dark:text-gray-100">{content.tone}</p>
          </div>
        )}
        {content.perspective && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Perspective</h4>
            <p className="mt-1 text-gray-900 dark:text-gray-100">{content.perspective}</p>
          </div>
        )}
        {content.characteristics?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Characteristics</h4>
            <ul className="mt-2 space-y-3">
              {content.characteristics.map((char, idx) => (
                <li key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <div className="font-medium text-obn-blue dark:text-white">{char.trait}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{char.description}</div>
                  {char.examples?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Examples: {char.examples.join(' | ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderEmojiContent = (content) => {
    if (!content) return <p className="text-gray-500 dark:text-gray-400 italic">No emoji configuration set</p>;
    return (
      <div className="space-y-4">
        {content.maxPerBlurb !== undefined && (
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-3">
            <span className="font-medium text-yellow-800 dark:text-yellow-200">Max per blurb:</span>{' '}
            <span className="text-yellow-700 dark:text-yellow-300">{content.maxPerBlurb}</span>
          </div>
        )}
        {content.placementRules?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Placement Rules</h4>
            <ul className="mt-2 space-y-1">
              {content.placementRules.map((rule, idx) => (
                <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start">
                  <span className="text-gray-400 dark:text-gray-500 mr-2">•</span>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        )}
        {content.approved?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Approved Emojis</h4>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {content.approved.map((emoji, idx) => (
                <div key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2 flex items-center space-x-3">
                  <span className="text-2xl">{emoji.emoji}</span>
                  <div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">{emoji.context}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 capitalize">{emoji.frequency}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPhrasesContent = (content, type) => {
    if (!content?.phrases?.length) {
      return <p className="text-gray-500 dark:text-gray-400 italic">No phrases configured</p>;
    }
    return (
      <div className="space-y-2">
        {content.category && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">Category: {content.category}</div>
        )}
        {content.phrases.map((phrase, idx) => (
          <div
            key={idx}
            className={`rounded-lg p-3 ${type === 'use' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50'}`}
          >
            <div className={`font-medium ${type === 'use' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
              "{phrase.phrase}"
            </div>
            {phrase.context && (
              <div className={`text-sm mt-1 ${type === 'use' ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                {phrase.context}
              </div>
            )}
            {phrase.alternatives?.length > 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Alternatives: {phrase.alternatives.join(', ')}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderFormattingContent = (content) => {
    if (!content) return <p className="text-gray-500 dark:text-gray-400 italic">No formatting rules set</p>;
    return (
      <div className="space-y-4">
        {content.structure?.wordLimits && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded-lg p-3">
            <h4 className="font-medium text-blue-800 dark:text-blue-200">Word Limits</h4>
            <div className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              {content.structure.wordLimits.blurb && (
                <div>
                  Blurb: {content.structure.wordLimits.blurb.min}-
                  {content.structure.wordLimits.blurb.max} words
                </div>
              )}
              {content.structure.wordLimits.intro && (
                <div>
                  Intro: {content.structure.wordLimits.intro.min}-
                  {content.structure.wordLimits.intro.max} words
                </div>
              )}
            </div>
          </div>
        )}
        {content.structure?.blurbFormula?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Blurb Formula</h4>
            <ol className="mt-2 space-y-1">
              {content.structure.blurbFormula.map((step, idx) => (
                <li key={idx} className="text-sm text-gray-600 dark:text-gray-300 flex items-start">
                  <span className="text-obn-blue dark:text-blue-400 font-medium mr-2">{idx + 1}.</span>
                  {step}
                </li>
              ))}
            </ol>
          </div>
        )}
        {content.rules?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">Formatting Rules</h4>
            <ul className="mt-2 space-y-2">
              {content.rules.map((rule, idx) => (
                <li key={idx} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-gray-100">{rule.rule}</span>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        rule.priority === 'required'
                          ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200'
                          : rule.priority === 'recommended'
                            ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-200'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {rule.priority}
                    </span>
                  </div>
                  {rule.examples?.length > 0 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Examples: {rule.examples.join(', ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (isEditing) {
      return (
        <div className="space-y-4">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            className="w-full h-96 font-mono text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-obn-blue focus:border-obn-blue dark:focus:ring-blue-400 dark:focus:border-blue-400"
            placeholder="Enter JSON content..."
          />
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending || createMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4 mr-2" />
              Save
            </button>
          </div>
        </div>
      );
    }

    const content = currentGuide?.content;
    switch (activeTab) {
      case 'voice':
        return renderVoiceContent(content);
      case 'emojis':
        return renderEmojiContent(content);
      case 'phrases:use':
        return renderPhrasesContent(content, 'use');
      case 'phrases:avoid':
        return renderPhrasesContent(content, 'avoid');
      case 'formatting':
        return renderFormattingContent(content);
      default:
        return <pre className="text-sm">{JSON.stringify(content, null, 2)}</pre>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading style guides...</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-obn-blue dark:text-white">Style Guide</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Configure voice, tone, and writing rules for AI generation
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-obn-blue hover:bg-obn-blue/90"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit {STYLE_GUIDE_TABS.find((t) => t.id === activeTab)?.label}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {STYLE_GUIDE_TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const hasContent = !!coreGuides?.[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setIsEditing(false);
                }}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                  ${
                    isActive
                      ? 'border-obn-blue text-obn-blue dark:border-blue-400 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.label}</span>
                {!hasContent && <span className="text-xs text-gray-400 dark:text-gray-500">(empty)</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 shadow dark:ring-1 dark:ring-white/10 rounded-lg p-6">
        {currentGuide && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Version {currentGuide.version} • Last modified:{' '}
            {new Date(currentGuide.modified).toLocaleString()}
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
}

export default StyleGuidePage;
